import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as api from '../api/portal';
import { getToken, setToken, setUnauthorizedHandler } from '../utils/token';
import type { Account } from '../types/portal';

// ----------------------------------------------------------------------

type AuthState = {
  account: Account | null;
  /** True until the stored token has been checked against the server. */
  isInitialising: boolean;
  isAuthenticated: boolean;
  isEmployer: boolean;
  isEmployee: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<Account>;
  register: (input: api.RegisterInput) => Promise<Account>;
  logout: () => void;
  refresh: () => Promise<void>;
  setAccount: (account: Account) => void;
};

const AuthContext = createContext<AuthState | null>(null);

// ----------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccountState] = useState<Account | null>(null);
  const [isInitialising, setIsInitialising] = useState(Boolean(getToken()));

  const logout = useCallback(() => {
    setToken(null);
    setAccountState(null);
  }, []);

  // A 401 from any request means the token is dead — drop it.
  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  // Resume the session on first load.
  useEffect(() => {
    let cancelled = false;

    if (!getToken()) {
      setIsInitialising(false);
      return undefined;
    }

    api
      .fetchMe()
      .then((me) => {
        if (!cancelled) setAccountState(me);
      })
      .catch(() => {
        if (!cancelled) setToken(null);
      })
      .finally(() => {
        if (!cancelled) setIsInitialising(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.login(email, password);
    setToken(response.access_token);
    setAccountState(response.account);
    return response.account;
  }, []);

  const register = useCallback(async (input: api.RegisterInput) => {
    const response = await api.register(input);
    setToken(response.access_token);
    setAccountState(response.account);
    return response.account;
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    setAccountState(await api.fetchMe());
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      account,
      isInitialising,
      isAuthenticated: Boolean(account),
      isEmployer: account?.role === 'employer',
      isEmployee: account?.role === 'employee',
      isAdmin: account?.role === 'admin',
      login,
      register,
      logout,
      refresh,
      setAccount: setAccountState,
    }),
    [account, isInitialising, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ----------------------------------------------------------------------

export function useAuth(): AuthState {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }

  return context;
}
